import {
  AuthProvider,
  deleteUser,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  OAuthProvider,
  User,
  UserCredential,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  reload,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { auth } from "../firebase";
import { getCurrentLocale } from "../localization";
import type { SignupAuthMethod } from "../constants/accountAcquisition";
import { accountProvisioningService } from "./accountProvisioningService";
import { assertAdultBirthDate } from "../utils/adultEligibility";

export type AuthStateUnsubscribe = () => void;
export type AuthErrorContext =
  | "appleSignIn"
  | "googleSignIn"
  | "emailSignIn"
  | "emailSignUp"
  | "passwordReset"
  | "emailVerification";

const createAppleProvider = (): OAuthProvider => {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  return provider;
};

const createGoogleProvider = (): GoogleAuthProvider => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
};

const normalizeAuthErrorCode = (value: unknown): string | null => {
  if (typeof value === "string" && value.startsWith("auth/")) {
    return value;
  }

  if (
    value &&
    typeof value === "object" &&
    "code" in value &&
    typeof (value as { code?: unknown }).code === "string"
  ) {
    const code = (value as { code: string }).code;
    if (code.startsWith("auth/")) {
      return code;
    }
  }

  if (
    value &&
    typeof value === "object" &&
    "message" in value &&
    typeof (value as { message?: unknown }).message === "string"
  ) {
    const message = (value as { message: string }).message;
    const match = message.match(/auth\/[a-z-]+/i);
    return match ? match[0].toLowerCase() : null;
  }

  return null;
};

let pendingAuthentication: Promise<void> | null = null;
const verificationNotices = new Map<string, "sent" | "failed">();

const withAuthentication = async <T>(operation: () => Promise<T>): Promise<T> => {
  if (pendingAuthentication) {
    throw Object.assign(new Error("Please wait for the current sign-in attempt to finish."), { code: "auth/operation-in-progress" });
  }
  let finish!: () => void;
  pendingAuthentication = new Promise<void>((resolve) => { finish = resolve; });
  try {
    return await operation();
  } finally {
    pendingAuthentication = null;
    finish();
  }
};

export const isAccountProvisioningError = (error: unknown): boolean =>
  normalizeAuthErrorCode(error) === "auth/account-provisioning-failed";

export const isAuthCancellation = (error: unknown): boolean =>
  ["auth/popup-closed-by-user", "auth/cancelled-popup-request"].includes(normalizeAuthErrorCode(error) || "");

export const getSignupAuthMethod = (user: User): SignupAuthMethod => {
  const providers = user.providerData || [];
  if (providers.some((provider) => provider.providerId === "password")) return "password";
  if (providers.some((provider) => provider.providerId === "apple.com")) return "apple";
  return "google";
};

const createProvisioningError = (
  message: string,
  cause: unknown
): Error & { code: string; cause: unknown } => {
  const error = new Error(message) as Error & { code: string; cause: unknown };
  error.name = "AccountProvisioningError";
  error.code = "auth/account-provisioning-failed";
  error.cause = cause;
  return error;
};

const rollbackNewAccount = async (user: User): Promise<boolean> => {
  let authUserDeleted = false;
  try {
    await deleteUser(user);
    authUserDeleted = true;
  } catch {
    // A failed delete must not leave this browser in the partially-created session.
  }

  try {
    await signOut(auth);
  } catch {
    // Preserve the original provisioning error for the signup UI.
  }
  return authUserDeleted;
};

const signInWithExistingSocialAccount = async (
  provider: AuthProvider,
  providerName: "Apple" | "Google"
): Promise<UserCredential> => {
  const credential = await signInWithPopup(auth, provider);
  const isNewUser = getAdditionalUserInfo(credential)?.isNewUser === true;

  if (isNewUser) {
    const authUserDeleted = await rollbackNewAccount(credential.user);
    const error = new Error(
      authUserDeleted
        ? `No RAUXA account is linked to this ${providerName} account. Use Sign Up to create one first.`
        : `Account setup was not completed, and automatic cleanup failed. Use Sign Up to finish creating your RAUXA profile or contact support.`
    ) as Error & { code: string };
    error.code = "auth/user-not-found";
    throw error;
  }

  return credential;
};

const signUpWithSocialAccount = async (
  provider: AuthProvider,
  signupAuthMethod: Extract<SignupAuthMethod, "apple" | "google">,
  birthDate: Date,
  displayName?: string
): Promise<UserCredential> => {
  assertAdultBirthDate(birthDate);
  const credential = await signInWithPopup(auth, provider);
  try {
    await accountProvisioningService.ensureAccountProvisioned({
      user: credential.user, signupAuthMethod, displayName, birthDate, acceptedTerms: true,
    });
    return credential;
  } catch (error) {
    // Keep the identity so a retry can finish the transaction. A timeout may be
    // ambiguous: deleting Auth here could orphan an already committed profile.
    throw createProvisioningError("Your sign-in is ready. Finish account setup to continue.", error);
  }
};

export const formatAuthErrorMessage = (
  error: unknown,
  context: AuthErrorContext,
  fallbackMessage: string
): string => {
  const code = normalizeAuthErrorCode(error);

  if (code === "auth/operation-not-allowed" || code === "auth/unauthorized-domain") {
    return "This sign-in method is currently unavailable. Try another method or contact support.";
  }
  if (code === "auth/operation-in-progress" || code === "auth/cancelled-popup-request") {
    return "Please wait for the current sign-in attempt to finish.";
  }
  if (code === "auth/popup-closed-by-user") return "Sign-in was canceled.";
  if (code === "auth/popup-blocked") {
    return "Your browser blocked the sign-in popup. Allow popups for this site and try again.";
  }
  if (code === "auth/account-provisioning-failed") return "Your sign-in is ready. Finish account setup to continue.";
  if (code === "auth/account-data-invalid") return "Your account setup needs assistance. Contact support.";
  if (code === "auth/invalid-display-name") return "Please enter a display name.";
  if (code === "auth/minimum-age-required") return "You must be at least 18 years old.";

  if (code === "auth/account-exists-with-different-credential") {
    return "This email uses another sign-in method. Log in with your original method, or reset your password if you used email.";
  }

  if (
    code === "auth/invalid-credential" &&
    (context === "appleSignIn" || context === "googleSignIn")
  ) {
    return "The sign-in request expired. Please try again.";
  }

  if (code === "auth/invalid-credential" && context === "emailSignIn") {
    return "Invalid email or password.";
  }

  if (code === "auth/invalid-login-credentials") {
    return "Invalid email or password.";
  }

  if (code === "auth/email-already-in-use") {
    return "An account with this email already exists.";
  }

  if (code === "auth/weak-password") {
    return "Password should be at least 6 characters.";
  }

  if (code === "auth/invalid-email") {
    return "Please enter a valid email address.";
  }

  if (code === "auth/user-disabled") {
    return "This account has been disabled. Contact support for help.";
  }

  if (code === "auth/too-many-requests") {
    return "Too many attempts. Please wait a moment and try again.";
  }

  if (code === "auth/network-request-failed") {
    return "A network error occurred. Check your connection and try again.";
  }

  if (code === "auth/user-not-found" && context === "passwordReset") {
    return "No account was found with that email address.";
  }

  if (code === "auth/user-not-found") return "No account was found. Use Sign Up to create an account.";
  if (code === "auth/requires-recent-login") return "Please log in again before continuing.";
  // SDK messages are not user-facing copy and may leak configuration details.
  return fallbackMessage;
};

export const authService = {
  onAuthStateChanged(listener: (user: User | null) => void): AuthStateUnsubscribe {
    return onAuthStateChanged(auth, listener);
  },
  getCurrentUser(): User | null {
    return auth.currentUser;
  },
  whenAuthenticationSettled(): Promise<void> {
    return pendingAuthentication || Promise.resolve();
  },
  getVerificationNotice(uid: string): "sent" | "failed" | undefined {
    return verificationNotices.get(uid);
  },
  async sendCurrentUserVerification(): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw Object.assign(new Error("Please log in again before continuing."), { code: "auth/requires-recent-login" });
    if (user.emailVerified) return;
    auth.languageCode = getCurrentLocale();
    try {
      await sendEmailVerification(user);
      verificationNotices.set(user.uid, "sent");
    } catch (error) {
      verificationNotices.set(user.uid, "failed");
      throw error;
    }
  },
  async refreshEmailVerification(): Promise<boolean> {
    const user = auth.currentUser;
    if (!user) return false;
    await reload(user);
    if (auth.currentUser?.uid !== user.uid) return false;
    const token = await user.getIdTokenResult(true);
    return auth.currentUser?.uid === user.uid && user.emailVerified && token.claims.email_verified === true;
  },
  createUser(email: string, password: string, displayName: string, birthDate: Date): Promise<UserCredential> {
    return withAuthentication(async () => {
      assertAdultBirthDate(birthDate);
      const normalizedDisplayName = displayName.trim();
      if (!normalizedDisplayName) throw Object.assign(new Error("Please enter a display name."), { code: "auth/invalid-display-name" });
      if (password.length < 6) throw Object.assign(new Error("Password should be at least 6 characters."), { code: "auth/weak-password" });
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      try {
        await updateProfile(credential.user, { displayName: normalizedDisplayName });
        await accountProvisioningService.ensureAccountProvisioned({
          user: credential.user, displayName: normalizedDisplayName, birthDate,
          signupAuthMethod: "password", acceptedTerms: true,
        });
      } catch (error) {
        throw createProvisioningError("Your sign-in is ready. Finish account setup to continue.", error);
      }
      // Email delivery is independent of account creation and can be retried.
      try { await authService.sendCurrentUserVerification(); } catch { /* shown by verification UI */ }
      return credential;
    });
  },
  signIn(email: string, password: string): Promise<UserCredential> {
    return withAuthentication(() => signInWithEmailAndPassword(auth, email.trim(), password));
  },
  signInWithApple(): Promise<UserCredential> {
    return withAuthentication(() => signInWithExistingSocialAccount(createAppleProvider(), "Apple"));
  },
  signUpWithApple(birthDate: Date, displayName?: string): Promise<UserCredential> {
    return withAuthentication(() => signUpWithSocialAccount(createAppleProvider(), "apple", birthDate, displayName));
  },
  signInWithGoogle(): Promise<UserCredential> {
    return withAuthentication(() => signInWithExistingSocialAccount(createGoogleProvider(), "Google"));
  },
  signUpWithGoogle(birthDate: Date, displayName?: string): Promise<UserCredential> {
    return withAuthentication(() => signUpWithSocialAccount(createGoogleProvider(), "google", birthDate, displayName));
  },
  async signOut(): Promise<void> {
    try {
      const { disableBrowserPushBeforeSignOut } = await import("../push/browserPushService");
      await disableBrowserPushBeforeSignOut();
    } catch {
      throw new Error("Could not sign out. Please try again.");
    }
    await signOut(auth);
  },
  async updateCurrentUserProfile(input: {
    displayName?: string | null;
    photoURL?: string | null;
    expectedUserId?: string;
  }): Promise<void> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("No authenticated user was found.");
    }

    if (input.expectedUserId && currentUser.uid !== input.expectedUserId) {
      throw new Error("No authenticated user was found.");
    }

    await updateProfile(currentUser, {
      displayName: input.displayName,
      photoURL: input.photoURL,
    });
  },
  async deleteCurrentUser(): Promise<void> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("No authenticated user was found.");
    }

    await deleteUser(currentUser);
  },
  async sendPasswordReset(email: string): Promise<void> {
    auth.languageCode = getCurrentLocale();
    await sendPasswordResetEmail(auth, email.trim());
  },
};
